import { actions } from "@mixitone/actions";
import { Account, AccountUser, type FundingOutreach, Funder } from "@mixitone/models";
import { formatEdgeFunctionError } from "@mixitone/util";
import { FormController } from "components/Forms/FormController";
import type { FormFieldConfig } from "components/Forms/FormFieldsDefinition";
import { FUNDER_TYPE_SELECT_OPTIONS } from "lib/fundingStatusLabels";
import { createCrossSupabase } from "lib/supabaseClient";
import type { RealtimeChannel } from "@supabase/supabase-js";

interface Props {
  clubId: string;
  id?: string;
  attributes?: Attributes;
  /** Used by inline funder edit inside the outreach drawer; not read by the controller. */
  onCancelEdit?: () => void;
}

type Attributes = object;

type FunderEnrichmentStatus = "idle" | "running" | "completed" | "error";

interface State {
  outreach: FundingOutreach[];
  enrichmentStatus: FunderEnrichmentStatus;
  enrichmentMessage: string | null;
  enrichmentError: string | null;
  enrichmentHadUpdates: boolean;
}

class FunderFormController extends FormController<Funder, Attributes, State, Props> {
  private enrichmentJobChannel: RealtimeChannel | null = null;

  static override initialState = {
    ...FormController.initialState,
    outreach: [] as FundingOutreach[],
    enrichmentStatus: "idle" as FunderEnrichmentStatus,
    enrichmentMessage: null as string | null,
    enrichmentError: null as string | null,
    enrichmentHadUpdates: false,
  };

  override destroy() {
    this.stopEnrichmentJobSubscription();
    super.destroy();
  }

  get canEnrichFromWebsite(): boolean {
    const record = this.state.record as Funder | undefined;
    return Boolean(record?.id && record.website?.trim() && this.state.enrichmentStatus !== "running");
  }

  override async buildRecord(): Promise<Funder> {
    return new Funder({
      club_id: this.props.clubId,
      account_id: Account.current!.id!,
      funder_type: "council",
    });
  }

  override findModel(id: string): Promise<Funder> {
    return Funder.query().find(id);
  }

  override async onLoad(): Promise<void> {
    const { record, newRecord } = this.state;
    if (record && !newRecord && record.id) {
      const funder = record as Funder;
      const outreach = await funder.outreach.query.eq("is_deleted", false).preload("funding_appeal").all();
      this.setState({ outreach: outreach as FundingOutreach[] });
    } else {
      this.setState({ outreach: [] });
    }
  }

  override async changeProps(newProps: Props) {
    await super.changeProps({
      ...newProps,
      attributes: newProps.attributes ?? {},
    } as any);
  }

  private stopEnrichmentJobSubscription() {
    if (!this.enrichmentJobChannel) return;
    void this.enrichmentJobChannel.unsubscribe();
    this.enrichmentJobChannel = null;
  }

  private async subscribeToEnrichmentJob(jobId: string) {
    this.stopEnrichmentJobSubscription();
    const supabase = await createCrossSupabase();
    this.enrichmentJobChannel = supabase
      .channel(`funder-enrichment-${jobId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "jobs",
          filter: `id=eq.${jobId}`,
        },
        (payload) => {
          void this.handleEnrichmentJobUpdate(
            payload.new as {
              status?: string;
              message?: string | null;
              response?: { updatedFields?: string[]; patch?: Record<string, string> } | null;
            },
          );
        },
      )
      .subscribe();
  }

  private async handleEnrichmentJobUpdate(job: {
    status?: string;
    message?: string | null;
    response?: { updatedFields?: string[]; patch?: Record<string, string> } | null;
  }) {
    if (job.message) {
      this.state.enrichmentMessage = job.message;
    }

    if (job.status === "processing" || job.status === "pending") {
      this.state.enrichmentStatus = "running";
      return;
    }

    this.stopEnrichmentJobSubscription();

    if (job.status === "completed") {
      const record = this.state.record as Funder | undefined;
      const updatedFields = job.response?.updatedFields ?? [];
      const patch = job.response?.patch;

      if (record && patch && updatedFields.length > 0) {
        for (const key of updatedFields) {
          const value = patch[key];
          if (value != null) {
            (record as unknown as Record<string, unknown>)[key] = value;
          }
        }
      } else if (record?.id) {
        const refreshed = await Funder.query().find(record.id);
        this.state.record = refreshed;
      }

      this.state.enrichmentHadUpdates = updatedFields.length > 0;
      this.state.enrichmentStatus = "completed";
      this.state.enrichmentError = null;
      return;
    }

    if (job.status === "error") {
      this.state.enrichmentStatus = "error";
      this.state.enrichmentError = job.message ?? "Enrichment failed";
    }
  }

  async actionEnrichFromWebsite() {
    const record = this.state.record as Funder | undefined;
    if (!record?.id || !record.website?.trim() || this.state.enrichmentStatus === "running") {
      return;
    }

    this.state.enrichmentStatus = "running";
    this.state.enrichmentMessage = "Enrichment queued…";
    this.state.enrichmentError = null;
    this.state.enrichmentHadUpdates = false;

    try {
      const { jobId } = await actions.funding.startFunderEnrichment({ funderId: record.id });
      await this.subscribeToEnrichmentJob(jobId);
    } catch (error) {
      this.state.enrichmentStatus = "error";
      this.state.enrichmentError = formatEdgeFunctionError(
        error,
        undefined,
        "Could not start enrichment. Please try again.",
      );
    }
  }

  override async saveRecord(record: Funder): Promise<void> {
    const r = record as Funder & { created_by_account_user_id?: string };
    if (!r.account_id) r.account_id = Account.current!.id!;
    if (!r.club_id) r.club_id = this.props.clubId;
    if (this.state.newRecord && AccountUser.current?.id) {
      r.created_by_account_user_id = AccountUser.current.id;
    }
    await record.save();
  }

  fields(): Record<string, FormFieldConfig> {
    return {
      name: {
        type: "text",
        name: "name",
        label: "Name",
        required: true,
        group: "basic",
      },
      funder_type: {
        type: "select",
        name: "funder_type",
        label: "Type",
        options: FUNDER_TYPE_SELECT_OPTIONS,
        group: "basic",
      },
      contact_name: {
        type: "text",
        name: "contact_name",
        label: "Contact name",
        group: "basic",
      },
      contact_email: {
        type: "text",
        name: "contact_email",
        label: "Contact email",
        inputType: "email",
        group: "basic",
      },
      contact_phone: {
        type: "text",
        name: "contact_phone",
        label: "Contact phone",
        inputType: "tel",
        group: "basic",
      },
      website: {
        type: "text",
        name: "website",
        label: "Website",
        inputType: "url",
        group: "basic",
      },
      note: {
        type: "text",
        name: "note",
        label: "Note",
        inputType: "textarea",
        group: "basic",
      },
    } as const satisfies Record<string, FormFieldConfig>;
  }
}

export { FunderFormController };
